import { createAction, props } from '@ngrx/store';
import { AccountSyncStatus, IAccount, ICreateSignaturePayload, IMailboxSyncResult, ISignature, IUpdateSignaturePayload, MailboxType } from '../models/account.models';
import { InboxFolderType, ISmartInboxStateType, IFolderKPI } from '../models/inbox.models';
import { APIResponse } from 'src/app/core/models/api-response';
import { IGetConversationsParams, IConversation, IConversationDetails, IConversationUpdate, ISentMessage, IGetSentMessagesParams, IMessageClassificationUpdate, IMessageClassificationResult } from '../models/conversation.models';
import { IComposePanel, IComposePanelAttachment } from '../models/compose.models';
import { IDraft, IGetDraftsParams } from '../models/draft.models';
import { IMention } from '../models/mention.models';
import { IInboxFilter } from './smart-inbox.reducers';
import { ITimelineComment } from '../models/timeline.models';

// ─── Connection State ────────────────────────────────────────────────────────

export const SetConnectionState = createAction(
  '[ Smart Inbox ] - SetConnectionState',
  props<{ connectionState: ISmartInboxStateType }>()
);

// ─── Accounts (Load) ─────────────────────────────────────────────────────────

export const LoadAccounts = createAction('[ Smart Inbox ] - LoadAccounts');
export const LoadAccountsSuccess = createAction(
  '[ Smart Inbox ] - LoadAccounts Success',
  props<{ accounts: APIResponse<IAccount> }>()
);
export const LoadAccountsError = createAction('[ Smart Inbox ] - LoadAccounts Error');

// ─── Accounts (Add New via Connection Wizard) ────────────────────────────────

export const AddNewAccount = createAction('[ Smart Inbox ] - AddNewAccount');
export const AddNewAccountSuccess = createAction(
  '[ Smart Inbox ] - AddNewAccount Success',
  props<{ accounts: IAccount[] }>()
);
export const AddNewAccountError = createAction('[ Smart Inbox ] - AddNewAccount Error');

// ─── Accounts (Update) ──────────────────────────────────────────────────────

export const UpdateAccount = createAction(
  '[ Smart Inbox ] - UpdateAccount',
  props<{ account: IAccount; payload: { name?: string; mailbox_type?: MailboxType } }>()
);
export const UpdateAccountSuccess = createAction(
  '[ Smart Inbox ] - UpdateAccount Success',
  props<{ account: IAccount }>()
);
export const UpdateAccountError = createAction('[ Smart Inbox ] - UpdateAccount Error');

// ─── Accounts (Refresh Connection) ──────────────────────────────────────────

export const RefreshAccounts = createAction(
  '[ Smart Inbox ] - RefreshAccounts',
  props<{ accountsIds: number[] }>()
);
/**
 * The bulk `/sync` route answered. "Success" means the request completed, not
 * that every mailbox synced — `results` carries a row per mailbox and some of
 * them may have failed.
 */
export const RefreshAccountsSuccess = createAction(
  '[ Smart Inbox ] - RefreshAccounts Success',
  props<{ accountsIds: number[]; results: IMailboxSyncResult[] }>()
);
export const RefreshAccountsError = createAction('[ Smart Inbox ] - RefreshAccounts Error');

// ─── Accounts (Disconnect — reversible) ─────────────────────────────────────

/** Stops syncing, keeps everything. The permanent action is `DeleteAccount`. */
export const DisconnectAccount = createAction(
  '[ Smart Inbox ] - DisconnectAccount',
  props<{ account: IAccount }>()
);
export const DisconnectAccountSuccess = createAction(
  '[ Smart Inbox ] - DisconnectAccount Success',
  props<{ accountId: number }>()
);
export const DisconnectAccountError = createAction('[ Smart Inbox ] - DisconnectAccount Error');

// ─── Accounts (Delete — permanent) ──────────────────────────────────────────

/** Removes the account and every conversation it holds. Not reversible. */
export const DeleteAccount = createAction(
  '[ Smart Inbox ] - DeleteAccount',
  props<{ account: IAccount }>()
);
export const DeleteAccountSuccess = createAction(
  '[ Smart Inbox ] - DeleteAccount Success',
  props<{ accountId: number }>()
);
export const DeleteAccountError = createAction('[ Smart Inbox ] - DeleteAccount Error');

// ─── Accounts (Re-connect) ──────────────────────────────────────────────────

/**
 * Re-runs hosted auth for a disconnected account. The backend revives the existing row by
 * (email, owner), so the id is unchanged — which is why `AddNewAccount` cannot be reused
 * (it keeps only ids absent from state, and would report no new accounts).
 */
export const ReconnectAccount = createAction(
  '[ Smart Inbox ] - ReconnectAccount',
  props<{ account: IAccount }>()
);
export const ReconnectAccountSuccess = createAction(
  '[ Smart Inbox ] - ReconnectAccount Success',
  props<{ accountId: number }>()
);
export const ReconnectAccountError = createAction('[ Smart Inbox ] - ReconnectAccount Error');

// ─── Folder (KPI) ──────────────────────────────────────────────────────────

export const LoadFoldersKPIs = createAction(
  '[ Smart Inbox ] - LoadFoldersKPIs',
  props<{ accounts: IAccount[] }>()
);
export const LoadFoldersKPIsSuccess = createAction(
  '[ Smart Inbox ] - LoadFoldersKPIs Success',
  props<{ kpis: IFolderKPI[] }>()
);
export const LoadFoldersKPIsError = createAction('[ Smart Inbox ] - LoadFoldersKPIs Error');

export const PatchFolderUnreadKPIs = createAction(
  '[ Smart Inbox ] - PatchFolderUnreadKPIs',
  props<{ folder: InboxFolderType; }>()
);

// ─── Filters ─────────────────────────────────────────────────────────────────

export const SetFilter = createAction(
  '[ Smart Inbox ] - SetFilter',
  props<{ filter: IInboxFilter }>()
);

// ─── Active Folder ──────────────────────────────────────────────────────────

export const SetActiveFolder = createAction(
  '[ Smart Inbox ] - SetActiveFolder',
  props<{ folder: InboxFolderType }>()
);

// ─── Conversations (Load) ────────────────────────────────────────────────────

export const LoadConversations = createAction(
  '[ Smart Inbox ] - LoadConversations',
  props<{ folder: InboxFolderType; params: IGetConversationsParams }>()
);
export const LoadConversationsSuccess = createAction(
  '[ Smart Inbox ] - LoadConversations Success',
  props<{ conversations: APIResponse<IConversation> }>()
);
export const LoadConversationsError = createAction('[ Smart Inbox ] - LoadConversations Error');

/** Emptied the list without asking the server: every mailbox in view is disconnected. */
export const SkipThreadListLoad = createAction('[ Smart Inbox ] - SkipThreadListLoad');

// ─── Conversations (Load More / Pagination) ─────────────────────────────────

export const LoadMoreConversations = createAction(
  '[ Smart Inbox ] - LoadMoreConversations',
  props<{ params: IGetConversationsParams }>()
);
export const LoadMoreConversationsSuccess = createAction(
  '[ Smart Inbox ] - LoadMoreConversations Success',
  props<{ conversations: APIResponse<IConversation> }>()
);
export const LoadMoreConversationsError = createAction('[ Smart Inbox ] - LoadMoreConversations Error');

// ─── Active Conversation ─────────────────────────────────────────────────────

export const SelectConversation = createAction(
  '[ Smart Inbox ] - SelectConversation',
  props<{ conversation: IConversation }>()
);
export const LoadConversationDetails = createAction(
  '[ Smart Inbox ] - LoadConversationDetails',
  props<{ conversationId: number }>()
);
export const LoadConversationDetailsSuccess = createAction(
  '[ Smart Inbox ] - LoadConversationDetails Success',
  props<{ details: IConversationDetails }>()
);
export const LoadConversationDetailsError = createAction('[ Smart Inbox ] - LoadConversationDetails Error');

export const LoadConversationFromUrl = createAction(
  '[ Smart Inbox ] - LoadConversationFromUrl',
  props<{ conversationId: number }>()
);

export const ClearActiveConversation = createAction('[ Smart Inbox ] - ClearActiveConversation');

// ─── Conversation Selection (Bulk) ──────────────────────────────────────────

export const ToggleConversationSelection = createAction(
  '[ Smart Inbox ] - ToggleConversationSelection',
  props<{ conversationId: number }>()
);
export const ToggleAllConversationsSelection = createAction('[ Smart Inbox ] - ToggleAllConversationsSelection');

/**
 * Set a whole run of conversations to one selection state.
 *
 * Exists for shift-range selection: resolving a range with
 * `ToggleConversationSelection` would mean one dispatch (and one reducer pass,
 * and one re-render) per row in the range, and would toggle rather than set, so
 * a range crossing an already-selected row would invert it instead of adopting
 * the clicked row's new state.
 */
export const SetConversationsSelection = createAction(
  '[ Smart Inbox ] - SetConversationsSelection',
  props<{ conversationIds: number[]; selected: boolean }>()
);
export const ClearSelection = createAction('[ Smart Inbox ] - ClearSelection');

// ─── Conversations (Update) ─────────────────────────────────────────────────

export const UpdateConversations = createAction(
  '[ Smart Inbox ] - UpdateConversations',
  props<{ conversationIds: number[]; payload: IConversationUpdate }>()
);
export const UpdateConversationsSuccess = createAction(
  '[ Smart Inbox ] - UpdateConversations Success',
  props<{ conversationIds: number[]; payload: IConversationUpdate; updatedDetails: IConversationDetails[] }>()
);
export const UpdateConversationsError = createAction('[ Smart Inbox ] - UpdateConversations Error');

// ─── Message Classification (category / intent override) ────────────────────

export const UpdateMessageClassification = createAction(
  '[ Smart Inbox ] - UpdateMessageClassification',
  props<{ conversationId: number; messageId: number; payload: IMessageClassificationUpdate }>()
);
export const UpdateMessageClassificationSuccess = createAction(
  '[ Smart Inbox ] - UpdateMessageClassification Success',
  props<{ conversationId: number; messageId: number; result: IMessageClassificationResult }>()
);
export const UpdateMessageClassificationError = createAction(
  '[ Smart Inbox ] - UpdateMessageClassification Error',
  props<{ messageId: number }>()
);

// ─── Thread Urgency (operator override) ─────────────────────────────────────

export const UpdateThreadUrgency = createAction(
  '[ Smart Inbox ] - UpdateThreadUrgency',
  props<{ conversationId: number; isUrgent: boolean }>()
);
export const UpdateThreadUrgencySuccess = createAction(
  '[ Smart Inbox ] - UpdateThreadUrgency Success',
  props<{ conversationId: number; isUrgent: boolean }>()
);
export const UpdateThreadUrgencyError = createAction('[ Smart Inbox ] - UpdateThreadUrgency Error');

// ─── Conversations (Mark Read) ──────────────────────────────────────────────

export const MarkConversationsRead = createAction(
  '[ Smart Inbox ] - MarkConversationsRead',
  props<{ conversationIds: number[]; isRead: boolean }>()
);
export const MarkConversationsReadSuccess = createAction(
  '[ Smart Inbox ] - MarkConversationsRead Success',
  props<{ conversationIds: number[]; isRead: boolean }>()
);
export const MarkConversationsReadError = createAction(
  '[ Smart Inbox ] - MarkConversationsRead Error',
  props<{ isRead?: boolean }>()
);

// ─── Create Comment ─────────────────────────────────────────────────────────

export const CreateComment = createAction(
  '[ Smart Inbox ] - CreateComment',
  props<{ conversationId: number; message: string }>()
);
export const CreateCommentSuccess = createAction(
  '[ Smart Inbox ] - CreateComment Success',
  props<{ conversationId: number; object: ITimelineComment }>()
);
export const CreateCommentError = createAction('[ Smart Inbox ] - CreateComment Error');

// ─── Update / Delete Comment ─────────────────────────────────────────────────

export const UpdateComment = createAction(
  '[ Smart Inbox ] - UpdateComment',
  props<{ conversationId: number; commentId: number; body: string }>()
);
export const UpdateCommentSuccess = createAction(
  '[ Smart Inbox ] - UpdateComment Success',
  props<{ conversationId: number; commentId: number; object: ITimelineComment }>()
);
export const UpdateCommentError = createAction('[ Smart Inbox ] - UpdateComment Error');

export const DeleteComment = createAction(
  '[ Smart Inbox ] - DeleteComment',
  props<{ conversationId: number; commentId: number }>()
);
export const DeleteCommentSuccess = createAction(
  '[ Smart Inbox ] - DeleteComment Success',
  props<{ conversationId: number; commentId: number }>()
);
export const DeleteCommentError = createAction('[ Smart Inbox ] - DeleteComment Error');

// ─── Realtime Events ────────────────────────────────────────────────────────

export const RealtimeEventReceived = createAction(
  '[ Smart Inbox ] - RealtimeEventReceived',
  props<{ conversationId: number }>()
);
/**
 * `sync_status_changed` off the mailbox SSE stream. The consumer that matters is the
 * reducer's per-account patch: `selectInboxSyncState` re-resolves the active accounts
 * against the live `accounts` list, so patching the one account is what makes the sync
 * banner live without a reload.
 */
export const MailboxSyncStatusChanged = createAction(
  '[ Smart Inbox ] - MailboxSyncStatusChanged',
  props<{
    mailboxId: number;
    syncStatus: AccountSyncStatus;
    lastSyncError: string | null;
    totalThreads: number;
  }>()
);
export const CommentMentionReceived = createAction(
  '[ Smart Inbox ] - CommentMentionReceived',
  props<{
    threadId: number;
    commentId: number;
    threadSubject?: string;
  }>()
);
export const ThreadAssignmentReceived = createAction(
  '[ Smart Inbox ] - ThreadAssignmentReceived',
  props<{
    threadIds: number[];
    authorId: number;
    timestamp: string;
  }>()
);
export const OrderCreatedReceived = createAction(
  '[ Smart Inbox ] - OrderCreatedReceived',
  props<{
    threadId: number;
    threadSubject?: string;
  }>()
);
export const UpsertConversation = createAction(
  '[ Smart Inbox ] - UpsertConversation',
  props<{ conversation: IConversation }>()
);

// ─── Send (Compose / Reply / Forward / Draft) ─────────────────────────────────────────────────

export const SendCompose = createAction(
  '[ Smart Inbox ] - SendCompose',
  props<{ panelId: string; replyToConversationId?: number }>()
);
export const SendComposeSuccess = createAction(
  '[ Smart Inbox ] - SendCompose Success',
  props<{ panelId: string; replyToConversationId?: number; draftId?: number | null }>()
);
export const SendComposeError = createAction(
  '[ Smart Inbox ] - SendCompose Error',
  props<{ panelId: string }>()
);

// ─── Drafts ─────────────────────────────────────────────────────────────────

export const LoadDrafts = createAction(
  '[ Smart Inbox ] - LoadDrafts',
  props<{ params: IGetDraftsParams }>()
);
export const LoadDraftsSuccess = createAction(
  '[ Smart Inbox ] - LoadDrafts Success',
  props<{ drafts: APIResponse<IDraft> }>()
);
export const LoadDraftsError = createAction('[ Smart Inbox ] - LoadDrafts Error');

export const LoadMoreDrafts = createAction(
  '[ Smart Inbox ] - LoadMoreDrafts',
  props<{ params: IGetDraftsParams }>()
);
export const LoadMoreDraftsSuccess = createAction(
  '[ Smart Inbox ] - LoadMoreDrafts Success',
  props<{ drafts: APIResponse<IDraft> }>()
);
export const LoadMoreDraftsError = createAction('[ Smart Inbox ] - LoadMoreDrafts Error');

export const CreateDraft = createAction(
  '[ Smart Inbox ] - CreateDraft',
  props<{ panel: IComposePanel }>()
);
export const CreateDraftSuccess = createAction(
  '[ Smart Inbox ] - CreateDraft Success',
  props<{ panelId: string; draft: IDraft }>()
);
export const CreateDraftError = createAction(
  '[ Smart Inbox ] - CreateDraft Error',
  props<{ panelId: string; error?: string }>()
);

export const UpdateDraft = createAction(
  '[ Smart Inbox ] - UpdateDraft',
  props<{ panel: IComposePanel; draftId: number }>()
);
export const UpdateDraftSuccess = createAction(
  '[ Smart Inbox ] - UpdateDraft Success',
  props<{ panelId: string; draft: IDraft }>()
);
export const UpdateDraftError = createAction(
  '[ Smart Inbox ] - UpdateDraft Error',
  props<{ panelId: string; error?: string }>()
);

export const DeleteDraft = createAction(
  '[ Smart Inbox ] - DeleteDraft',
  props<{ draftId: number }>()
);
export const DeleteDraftSuccess = createAction(
  '[ Smart Inbox ] - DeleteDraft Success',
  props<{ draftId: number }>()
);

// ─── Draft Attachments ──────────────────────────────────────────────────────

export const UploadDraftAttachments = createAction(
  '[ Smart Inbox ] - UploadDraftAttachments',
  props<{ panelId: string }>()
);

export const RemoveDraftAttachment = createAction(
  '[ Smart Inbox ] - RemoveDraftAttachment',
  props<{ panelId: string; attachment: IComposePanelAttachment }>()
);

// ─── AI Draft ─────────────────────────────────────────────────────────────

export const GenerateAiDraft = createAction(
  '[ Smart Inbox ] - GenerateAiDraft',
  props<{ panelId: string; conversationId: number }>()
);
export const GenerateAiDraftSuccess = createAction(
  '[ Smart Inbox ] - GenerateAiDraft Success',
  props<{ panelId: string; body: string | null; placeholders: { id: string; description?: string }[]; reasoning?: string | null; processingStatus?: string | null; processingErrors?: string | null }>()
);
export const GenerateAiDraftError = createAction(
  '[ Smart Inbox ] - GenerateAiDraft Error',
  props<{ panelId: string }>()
);

// ─── Mentions ───────────────────────────────────────────────────────────────

export const LoadMentions = createAction(
  '[ Smart Inbox ] - LoadMentions',
  props<{ params: { page: number; limit: number } }>()
);
export const LoadMentionsSuccess = createAction(
  '[ Smart Inbox ] - LoadMentions Success',
  props<{ mentions: APIResponse<IMention> }>()
);
export const LoadMentionsError = createAction('[ Smart Inbox ] - LoadMentions Error');

export const LoadMoreMentions = createAction(
  '[ Smart Inbox ] - LoadMoreMentions',
  props<{ params: { page: number; limit: number } }>()
);
export const LoadMoreMentionsSuccess = createAction(
  '[ Smart Inbox ] - LoadMoreMentions Success',
  props<{ mentions: APIResponse<IMention> }>()
);
export const LoadMoreMentionsError = createAction('[ Smart Inbox ] - LoadMoreMentions Error');

export const SelectConversationFromComment = createAction(
  '[ Smart Inbox ] - SelectConversationFromComment',
  props<{ mention: IMention }>()
);
export const ClearActiveCommentId = createAction('[ Smart Inbox ] - ClearActiveCommentId');

// ─── Sent Messages (Sent folder: flat list of outbound messages) ─────────────

export const LoadSentMessages = createAction(
  '[ Smart Inbox ] - LoadSentMessages',
  props<{ params: IGetSentMessagesParams }>()
);
export const LoadSentMessagesSuccess = createAction(
  '[ Smart Inbox ] - LoadSentMessages Success',
  props<{ messages: APIResponse<ISentMessage> }>()
);
export const LoadSentMessagesError = createAction('[ Smart Inbox ] - LoadSentMessages Error');

export const LoadMoreSentMessages = createAction(
  '[ Smart Inbox ] - LoadMoreSentMessages',
  props<{ params: IGetSentMessagesParams }>()
);
export const LoadMoreSentMessagesSuccess = createAction(
  '[ Smart Inbox ] - LoadMoreSentMessages Success',
  props<{ messages: APIResponse<ISentMessage> }>()
);
export const LoadMoreSentMessagesError = createAction('[ Smart Inbox ] - LoadMoreSentMessages Error');

/** Open a sent message's conversation and highlight that specific message in the timeline. */
export const SelectSentMessage = createAction(
  '[ Smart Inbox ] - SelectSentMessage',
  props<{ message: ISentMessage }>()
);
export const SetActiveMessageId = createAction(
  '[ Smart Inbox ] - SetActiveMessageId',
  props<{ messageId: number }>()
);
export const ClearActiveMessageId = createAction('[ Smart Inbox ] - ClearActiveMessageId');

// ─── Signatures ──────────────────────────────────────────────────────────────

export const LoadSignatures = createAction(
  '[ Smart Inbox ] - LoadSignatures',
  props<{ mailboxId: number }>()
);
export const LoadSignaturesSuccess = createAction(
  '[ Smart Inbox ] - LoadSignatures Success',
  props<{ signatures: ISignature[] }>()
);
export const LoadSignaturesError = createAction('[ Smart Inbox ] - LoadSignatures Error');

export const CreateSignature = createAction(
  '[ Smart Inbox ] - CreateSignature',
  props<{ mailboxId: number; payload: ICreateSignaturePayload }>()
);
export const CreateSignatureSuccess = createAction(
  '[ Smart Inbox ] - CreateSignature Success',
  props<{ mailboxId: number; signature: ISignature }>()
);
export const CreateSignatureError = createAction('[ Smart Inbox ] - CreateSignature Error');

export const UpdateSignature = createAction(
  '[ Smart Inbox ] - UpdateSignature',
  props<{ mailboxId: number; signatureId: number; payload: IUpdateSignaturePayload }>()
);
export const UpdateSignatureSuccess = createAction(
  '[ Smart Inbox ] - UpdateSignature Success',
  props<{ mailboxId: number; signature: ISignature }>()
);
export const UpdateSignatureError = createAction('[ Smart Inbox ] - UpdateSignature Error');

export const DeleteSignature = createAction(
  '[ Smart Inbox ] - DeleteSignature',
  props<{ mailboxId: number; signatureId: number }>()
);
export const DeleteSignatureSuccess = createAction(
  '[ Smart Inbox ] - DeleteSignature Success',
  props<{ mailboxId: number; signatureId: number }>()
);
export const DeleteSignatureError = createAction('[ Smart Inbox ] - DeleteSignature Error');

// ─── Create Order ──────────────────────────────────────────────────────────

export const CreateOrder = createAction(
  '[ Smart Inbox ] - CreateOrder',
  props<{ conversationId: number }>()
);
/** The API accepted the request (202); the order is NOT ready — it lands with the `order_created` event. */
export const CreateOrderAccepted = createAction(
  '[ Smart Inbox ] - CreateOrder Accepted',
  props<{ conversationId: number }>()
);
/**
 * A creation is already running for this thread (409) — nothing new started, so
 * the placeholder already on screen must stay up. No reducer case on purpose.
 */
export const CreateOrderAlreadyRunning = createAction(
  '[ Smart Inbox ] - CreateOrder Already Running',
  props<{ conversationId: number }>()
);
/**
 * Carries the thread: the user is often on another conversation by the time the
 * job fails. `queueFull` marks the 503 (nothing started, retrying is safe).
 *
 * `errorCode` is the review reason of the terminal `order_creation_failed`
 * event, and only that path carries the key at all — its presence is what tells
 * `createOrderError$` to render the explaining toast with a link back to the
 * thread, rather than the bare one a rejected POST warrants (the user is still
 * on the conversation, and an HTTP rejection has no reason to explain).
 */
export const CreateOrderError = createAction(
  '[ Smart Inbox ] - CreateOrder Error',
  props<{ conversationId: number; queueFull?: boolean; errorCode?: string | null }>()
);
